import { useEffect, useState } from "react";
import { useStore } from "zustand";

import { DevMenu } from "./DevMenu";
import {
  lossToLevel,
  normalizeLevel,
  rttToLevel,
  transportToLevel,
} from "../modules/connection-stats/ui/labels";
import { CHIP_HINTS } from "../lib/chip-hints";
import type { LinkQualityPayload, TelemetryField } from "../modules/teleop-state";
import type { TeleopSession } from "../modules/teleop-session";
import { useRobotState } from "../hooks/useRobotState";
import { StatusChip } from "./StatusChip";
import { LegSpark } from "../modules/connection-stats/ui/LegSpark";
import styles from "./OperatorHeader.module.scss";

export interface OperatorHeaderProps {
  sessionId: string;
  cameraEnabled: boolean[];
  cameraCount?: number;
  onReconnect: () => Promise<void> | void;
  onRestoreVideo: () => Promise<void> | void;
  onThemeChange: (label: string) => void;
  /** Expands ViewerPage into its video+controls-only fullscreen layout. */
  onEnterFullscreen: () => void;
  /** This session's transport store — subscribed directly for its connectionQuality
   * field (RTT/loss/transportState + history), per-session state, not a module global. */
  session: TeleopSession;
}

function formatNum(value: unknown, digits = 1): string {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toFixed(digits);
}

function formatTelemetryValue(field: TelemetryField): string {
  if (field.value == null) return "—";
  if (field.type === "number" && typeof field.value === "number") {
    const abs = Math.abs(field.value);
    const digits = abs !== 0 && abs < 1 ? 3 : abs < 10 ? 2 : 1;
    const text = Number.isInteger(field.value) ? String(field.value) : field.value.toFixed(digits);
    return field.unit ? `${text} ${field.unit}` : text;
  }
  if (field.type === "boolean") return field.value ? "да" : "нет";
  return field.unit ? `${field.value} ${field.unit}` : String(field.value);
}

function formatAbrProfile(uplink: LinkQualityPayload | null): string {
  if (!uplink?.abr_profile) return "—";
  const parts = [uplink.abr_profile];
  if (uplink.abr_fps != null) parts.push(`${uplink.abr_fps}fps`);
  if (uplink.abr_bitrate_kbps != null) parts.push(`${uplink.abr_bitrate_kbps}k`);
  return parts.join(" · ");
}

export function OperatorHeader({
  sessionId,
  cameraEnabled,
  cameraCount,
  onReconnect,
  onRestoreVideo,
  onThemeChange,
  onEnterFullscreen,
  session,
}: OperatorHeaderProps) {
  const robotState = useRobotState();

  const { snapshot: conn, history: downHistory } = useStore(session, (s) => s.connectionQuality);
  useStore(robotState.robotTelemetry); // subscribe to trigger re-render on new telemetry
  const uplink = useStore(robotState.linkQuality);
  const upHistory = robotState.linkQuality.getHistory();
  const [telemetryTick, setTelemetryTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTelemetryTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const ageMs = robotState.robotTelemetry.getAgeMs();
  const telemetryFields = robotState.robotTelemetry.getFields();
  void telemetryTick;

  const socketLevel = ageMs == null ? "muted" : ageMs < 3000 ? "good" : "critical";
  const socketValue = ageMs == null ? "—" : ageMs < 3000 ? "OK" : "нет данных";

  const activeCameras = cameraEnabled.filter(Boolean).length;
  const totalCameras = cameraCount ?? cameraEnabled.length ?? 0;

  const uplinkActive =
    Array.isArray(uplink?.active_sources) && uplink.active_sources.length
      ? uplink.active_sources.join("+")
      : "нет";

  const abrHint = uplink?.abr_profile
    ? `${CHIP_HINTS.abrProfile}\nИсточники: ${uplinkActive}`
    : `${CHIP_HINTS.abrProfile}\nНет abr_profile в link-quality — streamer ещё не получил ответ gst (:8591).`;

  const downSamples = downHistory.map((s) => ({
    lossPercentRecent: s.lossPercentRecent,
    rttMs: s.rttMs,
  }));
  const upSamples = upHistory.map((s) => ({
    loss_pct: s.loss_pct,
    rtt_ms: s.rtt_ms,
  }));

  const downLossDetail =
    conn.lossPercentRecent != null && conn.packetsLostRecent != null
      ? `За ~2 с, худшая камера: ${conn.packetsLostRecent} пак.`
      : "";
  const downLossHint = downLossDetail ? `${CHIP_HINTS.loss}\n${downLossDetail}` : CHIP_HINTS.loss;

  return (
    <header className={styles.root} id="operator-header">
      <span className={styles.room}>
        <i className="bi bi-robot" /> {sessionId}
      </span>
      <div className={styles.metricsStack}>
        <div className={styles.metricRow} aria-label="К оператору (SFU → UI)">
          <span className={styles.rowLabel} title="Downlink SFU → оператор">
            ↓ UI
          </span>
          <div className={styles.metrics}>
            <StatusChip
              id="chip-rtt"
              icon="bi-stopwatch"
              label="RTT"
              spark={<LegSpark kind="rtt" samples={downSamples} label="↓ UI RTT" />}
              value={conn.rttMs != null ? `${Math.round(conn.rttMs)} ms` : "—"}
              valueVariant="rtt"
              level={rttToLevel(conn.rttMs)}
              hint={CHIP_HINTS.rtt}
            />
            <StatusChip
              id="chip-loss"
              icon="bi-percent"
              label="Loss"
              spark={<LegSpark kind="loss" samples={downSamples} label="↓ UI Loss" />}
              value={
                conn.lossPercentRecent != null ? `${formatNum(conn.lossPercentRecent, 1)}%` : "—"
              }
              valueVariant="loss"
              level={lossToLevel(conn.lossPercentRecent)}
              hint={downLossHint}
            />
            <StatusChip
              id="chip-signaling"
              icon="bi-broadcast"
              label="Transport"
              value={conn.transportState || "—"}
              level={transportToLevel(conn.transportState)}
              hint={CHIP_HINTS.signaling}
            />
          </div>
        </div>

        <div className={styles.metricRow} aria-label="От робота (robot → SFU)">
          <span className={styles.rowLabel} title="Uplink robot → SFU">
            ↑ SFU
          </span>
          <div className={styles.metrics}>
            <StatusChip
              id="chip-uplink-rtt"
              icon="bi-stopwatch"
              label="RTT"
              spark={<LegSpark kind="rtt" samples={upSamples} label="↑ SFU RTT" />}
              value={uplink?.rtt_ms != null ? `${Math.round(Number(uplink.rtt_ms))} ms` : "—"}
              valueVariant="rtt"
              level={rttToLevel(uplink?.rtt_ms ?? null)}
              hint={CHIP_HINTS.uplinkRtt}
            />
            <StatusChip
              id="chip-uplink-loss"
              icon="bi-percent"
              label="Loss"
              spark={<LegSpark kind="loss" samples={upSamples} label="↑ SFU Loss" />}
              value={uplink?.loss_pct != null ? `${formatNum(uplink.loss_pct, 1)}%` : "—"}
              valueVariant="loss"
              level={lossToLevel(uplink?.loss_pct ?? null)}
              hint={`${CHIP_HINTS.uplinkLoss}\nИсточники: ${uplinkActive}`}
            />
            <StatusChip
              id="chip-uplink-transport"
              icon="bi-broadcast"
              label="Transport"
              value={uplink?.transport_state || "—"}
              level={transportToLevel(uplink?.transport_state)}
              hint={CHIP_HINTS.uplinkTransport}
            />
            <StatusChip
              id="chip-uplink-abr"
              icon="bi-sliders2-vertical"
              label="ABR"
              value={formatAbrProfile(uplink)}
              level={uplink?.abr_profile ? normalizeLevel(uplink.abr_profile) : "muted"}
              hint={abrHint}
            />
          </div>
        </div>

        <div className={styles.metricRow} aria-label="Робот">
          <span className={styles.rowLabel} title="Состояние робота">
            <i className="bi bi-robot" /> Bot
          </span>
          <div className={styles.metrics}>
            <StatusChip
              id="chip-cameras"
              icon="bi-camera-video"
              label="Камеры"
              value={`${activeCameras}/${totalCameras}`}
              level="good"
              hint={CHIP_HINTS.cameras}
            />
            <StatusChip
              id="chip-socketio"
              icon="bi-plug"
              label="Data bridge"
              value={socketValue}
              level={socketLevel}
              hint={CHIP_HINTS.socketio}
            />
            {telemetryFields.length === 0 ? (
              <StatusChip
                id="chip-bot-empty"
                icon="bi-hourglass"
                label="Телеметрия"
                value="нет данных"
                level="muted"
                hint="Ожидание пакета telemetry от робота."
              />
            ) : (
              telemetryFields.map((field) => (
                <StatusChip
                  key={field.key}
                  icon="bi-activity"
                  label={field.label}
                  value={formatTelemetryValue(field)}
                  level={null}
                  hint={`Показатель телеметрии от робота.\nКлюч: ${field.key}`}
                />
              ))
            )}
          </div>
        </div>
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.fullscreenButton}
          aria-label="Развернуть на весь экран"
          title="Развернуть на весь экран"
          onClick={onEnterFullscreen}
        >
          <i className="bi bi-arrows-fullscreen" aria-hidden="true" />
        </button>
        <DevMenu
          onReconnect={onReconnect}
          onRestoreVideo={onRestoreVideo}
          onThemeChange={onThemeChange}
        />
      </div>
    </header>
  );
}
